import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { PageMeta } from '@/components/PageMeta'
import { PageHeader } from '@/components/sections/PageHeader'
import { ContactForm } from '@/components/ContactForm'
import {
  Phone,
  Mail,
  Printer,
  Clock,
  MessageCircle,
  CheckCircle2,
  Send,
  HeadphonesIcon,
  Users,
  User,
  ArrowUpRight,
} from 'lucide-react'

export function ContactPage() {
  const { t } = useTranslation(['pages', 'common'])
  const sectionRef = useRef(null)
  const whyRef = useRef(null)
  const locationsRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const whyInView = useInView(whyRef, { once: true, margin: '-100px' })
  const locationsInView = useInView(locationsRef, { once: true, margin: '-100px' })
  const [activeLoc, setActiveLoc] = useState(0)

  const locations = [
    {
      name: t('common:locations.pravisdomini.name'),
      label: t('common:locations.pravisdomini.label'),
      address: t('common:locations.pravisdomini.address'),
      city: t('common:locations.pravisdomini.city'),
      phone: t('common:locations.pravisdomini.phone'),
      main: true,
    },
    {
      name: t('common:locations.sanVito.name'),
      label: t('common:locations.sanVito.label'),
      address: t('common:locations.sanVito.address'),
      city: t('common:locations.sanVito.city'),
      phone: t('common:locations.sanVito.phone'),
      main: false,
    },
    {
      name: t('common:locations.porcia.name'),
      label: t('common:locations.porcia.label'),
      address: t('common:locations.porcia.address'),
      city: t('common:locations.porcia.city'),
      phone: t('common:locations.porcia.phone'),
      main: false,
    },
    {
      name: t('common:locations.barletta.name'),
      label: t('common:locations.barletta.label'),
      address: t('common:locations.barletta.address'),
      city: t('common:locations.barletta.city'),
      phone: t('common:locations.barletta.phone'),
      main: false,
    },
    {
      name: t('common:locations.catania.name'),
      label: t('common:locations.catania.label'),
      address: t('common:locations.catania.address'),
      city: t('common:locations.catania.city'),
      phone: t('common:locations.catania.phone') || undefined,
      main: false,
    },
  ]

  const contactMethods = [
    { id: 'phone', icon: Phone, label: t('common:contacts.phone'), value: '+39 0434 645075', href: 'tel:+390434645075' },
    { id: 'fax', icon: Printer, label: t('common:contacts.fax'), value: '+39 0434 645505', href: 'tel:+390434645505' },
    { id: 'email', icon: Mail, label: t('common:contacts.email'), value: 'info@fgautomazioni.it', href: 'mailto:info@fgautomazioni.it' },
    { id: 'pec', icon: Mail, label: t('common:contacts.pec'), value: 'fgautomazioni@legalmail.it', href: 'mailto:fgautomazioni@legalmail.it' },
  ]

  const benefits = [
    { icon: Clock, title: t('contact.benefit1Title'), desc: t('contact.benefit1Desc') },
    { icon: MessageCircle, title: t('contact.benefit2Title'), desc: t('contact.benefit2Desc') },
    { icon: CheckCircle2, title: t('contact.benefit3Title'), desc: t('contact.benefit3Desc') },
  ]

  const managers = [
    { name: 'Franzin Giancarlo', role: t('contact.managers.giancarloRole'), email: 'giancarlo.f@fgautomazioni.it' },
    { name: 'Pozzobon Andrea', role: t('contact.managers.andreaRole'), email: 'andrea.p@fgautomazioni.it' },
    { name: 'Busdraghi Carlo', role: t('contact.managers.carloRole'), email: 'carlo.b@fgautomazioni.it' },
    { name: 'Parravicini Mario', role: t('contact.managers.marioRole'), email: 'mario.p@fgautomazioni.it' },
  ]

  return (
    <>
      <PageMeta title={`${t('contact.title')} — FG Automazioni`} description={t('contact.subtitle')} />

      {/* Hero Section */}
      <PageHeader
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
        tag={t('contact.tag')}
        icon={HeadphonesIcon}
        gradient="orange"
      />

      {/* Main Content — Contact Methods + Form + Info */}
      <section ref={sectionRef} className="bg-slate-50 pb-16 pt-10 md:pb-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          {/* Contact Methods */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-2 gap-4 md:grid-cols-4"
          >
            {contactMethods.map((method, index) => {
              const Icon = method.icon
              return (
                <motion.a
                  key={method.id}
                  href={method.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="group flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm transition-all duration-300 hover:border-[#f75e47]/30 hover:shadow-xl"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#f75e47]/10 text-[#f75e47] shadow-lg shadow-[#f75e47]/10 transition-all duration-300 group-hover:bg-[#f75e47] group-hover:text-white group-hover:shadow-[#f75e47]/30">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                      {method.label}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{method.value}</p>
                  </div>
                </motion.a>
              )
            })}
          </motion.div>

          {/* Form + Info */}
          <div className="mt-12 grid gap-12 lg:grid-cols-12 lg:gap-16 md:mt-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7"
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/50 md:p-10">
                <div className="mb-8 flex items-center gap-4 border-b border-slate-100 pb-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#f75e47] to-[#e04d36] text-white shadow-lg shadow-[#f75e47]/25">
                    <Send className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wider text-[#f75e47]">
                      {t('contact.writeNow')}
                    </span>
                    <h2 className="text-2xl font-bold text-slate-900">{t('contact.formTitle')}</h2>
                  </div>
                </div>
                <ContactForm />
              </div>
            </motion.div>

            {/* Info Sidebar */}
            <div className="space-y-6 lg:col-span-5">
              {/* Opening Hours */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-[#f75e47]/10 blur-3xl" />
                <div className="relative flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-[#f75e47]">
                    <Clock className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900">{t('contact.openingHours')}</h3>
                </div>
                <div className="relative mt-5 space-y-3">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-slate-600">{t('contact.monFri')}</span>
                    <span className="font-medium text-slate-900">08:30 - 18:00</span>
                  </div>
                  <div className="h-px bg-slate-100" />
                  <div className="flex items-center justify-between py-2">
                    <span className="text-slate-600">{t('contact.saturday')}</span>
                    <span className="font-medium text-slate-900">08:30 - 12:00</span>
                  </div>
                  <div className="h-px bg-slate-100" />
                  <div className="flex items-center justify-between py-2">
                    <span className="text-slate-400">{t('contact.sunday')}</span>
                    <span className="font-medium text-slate-400">{t('contact.closed')}</span>
                  </div>
                </div>
              </motion.div>

              {/* Managers */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#f75e47]/10 text-[#f75e47]">
                    <Users className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900">{t('contact.managersTitle')}</h3>
                </div>
                <div className="mt-5 space-y-4">
                  {managers.map((m) => (
                    <div key={m.email} className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                        <User className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">{m.name}</p>
                        <p className="text-sm text-slate-500">{m.role}</p>
                        <a
                          href={`mailto:${m.email}`}
                          className="mt-0.5 block truncate text-sm text-[#f75e47] hover:underline"
                        >
                          {m.email}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Us */}
      <section ref={whyRef} className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={whyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="mx-auto max-w-3xl text-center"
          >
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#f75e47]/10 px-4 py-1.5 text-sm font-medium text-[#f75e47]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#f75e47]" />
              FG Automazioni
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">{t('contact.whyContactTitle')}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
              {t('contact.whyContactSubtitle')}
            </p>
          </motion.div>

          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 40 }}
                  animate={whyInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -8 }}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.04),0_10px_30px_-5px_rgba(0,0,0,0.05)] transition-all duration-500 hover:border-[#f75e47]/20 hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1),0_10px_20px_-5px_rgba(247,94,71,0.15)]"
                >
                  <span className="pointer-events-none absolute right-6 top-4 font-display text-7xl font-bold text-slate-100 transition-colors group-hover:text-slate-200">
                    0{index + 1}
                  </span>

                  <div className="relative mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-[#f75e47] to-[#e04d36] text-white shadow-lg shadow-[#f75e47]/25 transition-all duration-300 group-hover:scale-110">
                    <Icon className="h-7 w-7" />
                  </div>

                  <h3 className="relative text-xl font-bold text-slate-900">{benefit.title}</h3>
                  <p className="relative mt-3 leading-relaxed text-slate-600">{benefit.desc}</p>

                  <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#f75e47] to-transparent transition-all duration-500 group-hover:w-full" />
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Locations — Industrial Atlas */}
      <section ref={locationsRef} className="relative overflow-hidden bg-[#faf8f6] py-20 md:py-28">
        {/* Technical grid background */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(#f75e47 1px, transparent 1px), linear-gradient(90deg, #f75e47 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -left-20 top-20 h-96 w-96 rounded-full bg-[#f75e47]/5 blur-[120px]" />
        <div className="absolute -right-20 bottom-20 h-96 w-96 rounded-full bg-slate-900/5 blur-[120px]" />

        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          {/* Header */}
          <div className="mb-16 flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={locationsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="mb-3 inline-block font-mono text-xs font-medium uppercase tracking-[0.25em] text-[#f75e47]">
                {t('contact.allLocations')}
              </span>
              <h2 className="font-display text-4xl font-bold tracking-tight text-slate-900 md:text-6xl">
                {t('contact.locationsTitle')}
              </h2>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={locationsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-sm text-right text-lg leading-relaxed text-slate-600 md:text-right"
            >
              {t('contact.allLocationsDesc')}
            </motion.p>
          </div>

          {/* Split layout */}
          <div className="grid items-stretch gap-8 lg:grid-cols-12 lg:gap-10">
            {/* Left — Location Index */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={locationsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="relative lg:col-span-5"
            >
              {/* Vertical connector line */}
              <div className="absolute left-[22px] top-10 bottom-10 w-px bg-gradient-to-b from-slate-200 via-slate-300 to-slate-200 md:left-[27px]" />

              <div className="relative space-y-2">
                {locations.map((loc, idx) => {
                  const isActive = activeLoc === idx
                  const code = ['PN-HQ', 'PN-02', 'PN-03', 'BT-01', 'CT-01'][idx]
                  return (
                    <button
                      key={loc.name}
                      onClick={() => setActiveLoc(idx)}
                      className="group relative z-10 w-full text-left outline-none"
                    >
                      <div
                        className={`flex items-start gap-5 py-5 transition-all duration-500 md:gap-6 md:py-6 ${
                          isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100'
                        }`}
                      >
                        {/* Number badge */}
                        <div
                          className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 font-mono text-sm font-bold transition-all duration-500 md:h-[3.25rem] md:w-[3.25rem] md:text-base ${
                            isActive
                              ? 'border-[#f75e47] bg-[#f75e47] text-white shadow-lg shadow-[#f75e47]/25'
                              : 'border-slate-300 bg-[#faf8f6] text-slate-500 group-hover:border-[#f75e47]/50'
                          }`}
                        >
                          {String(idx + 1).padStart(2, '0')}
                        </div>

                        {/* Content */}
                        <div className="flex-1 border-b border-slate-200 pb-5 transition-colors group-last:border-0 md:pb-6">
                          <div className="flex flex-wrap items-center gap-3">
                            <h3
                              className={`font-display text-2xl transition-colors duration-300 md:text-3xl ${
                                isActive ? 'text-slate-900' : 'text-slate-600 group-hover:text-slate-900'
                              }`}
                            >
                              {loc.name}
                            </h3>
                            {loc.main && (
                              <span className="rounded-full bg-[#f75e47] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
                                HQ
                              </span>
                            )}
                            <span className="font-mono text-xs text-slate-400">{code}</span>
                          </div>

                          <AnimatePresence initial={false}>
                            {isActive && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                className="overflow-hidden"
                              >
                                <p className="mt-2 text-sm font-semibold uppercase tracking-wider text-[#f75e47]">
                                  {loc.label}
                                </p>
                                <div className="mt-4 space-y-1 text-slate-600">
                                  <p className="text-base font-medium text-slate-800">{loc.address}</p>
                                  <p className="text-base">{loc.city}</p>
                                </div>

                                {loc.phone && (
                                  <a
                                    href={`tel:${loc.phone.replace(/\s/g, '')}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors hover:text-[#f75e47]"
                                  >
                                    <Phone className="h-4 w-4 text-[#f75e47]" />
                                    {loc.phone}
                                  </a>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </motion.div>

            {/* Right — Architectural Map Frame */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={locationsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="relative lg:col-span-7"
            >
              <div className="relative h-full min-h-[540px] overflow-hidden rounded-[2rem] bg-slate-900 shadow-2xl shadow-slate-900/20 md:min-h-[640px]">
                {/* Blueprint corner accents */}
                <div className="pointer-events-none absolute left-6 top-6 h-16 w-16 border-l border-t border-white/10 md:left-10 md:top-10 md:h-20 md:w-20" />
                <div className="pointer-events-none absolute bottom-6 right-6 h-16 w-16 border-b border-r border-white/10 md:bottom-10 md:right-10 md:h-20 md:w-20" />

                {/* HQ badge */}
                {locations[activeLoc].main && (
                  <div className="absolute right-6 top-6 z-20 md:right-10 md:top-10">
                    <div className="flex items-center gap-2 rounded-full bg-[#f75e47] px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg md:px-5">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
                      Sede Principale
                    </div>
                  </div>
                )}

                {/* Active location map */}
                <iframe
                  key={activeLoc}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(
                    locations[activeLoc].address + ', ' + locations[activeLoc].city
                  )}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={t('contact.mapTitle')}
                  className="absolute inset-0 transition-all duration-700"
                />

                {/* Bottom info bar */}
                <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-slate-900 via-slate-900/90 to-transparent px-6 pb-8 pt-20 md:px-10 md:pb-10">
                  <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-widest text-white/50">
                        {locations[activeLoc].label}
                      </p>
                      <p className="mt-1 text-lg font-medium text-white md:text-xl">
                        {locations[activeLoc].address}, {locations[activeLoc].city}
                      </p>
                    </div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        locations[activeLoc].address + ', ' + locations[activeLoc].city
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 transition-colors hover:text-[#f75e47]"
                    >
                      <ArrowUpRight className="h-4 w-4" />
                      Apri in Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
